import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at lines 69-70: keys are ${statNameLocaleKey}_one and ${statNameLocaleKey}_other; statNameLocaleKey is a prop (e.g. deviation.stats.faves).
// Add concrete keys below to exclude from unused report:
//   deviation.stats.faves_one
//   deviation.stats.faves_other
//   deviation.stats.comments_one
//   deviation.stats.comments_other
import { PapiDeviation } from '@wix/da-papi-types';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { Props as IconProps, IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import WhoTooltipWrapper from './WhoTooltipWrapper';
import { UserRelationType } from '../Who/redux/types';

import s from './Stat.scss';

export interface Props {
  deviation: PapiDeviation;
  /** locale key for the stat desicription (e.g. 'deviation.stats.faves') */
  statNameLocaleKey: string;
  /** the stat (e.g. number of favourites) */
  statCount?: number;
  /** don't display language after stat count */
  onlyNumbers?: boolean;
  /** Should this open a WhoTooltipWrapper that displays the users related to the stat */
  userListType?: UserRelationType;
  /** regular html tooltip text */
  title?: string;
  biData?: BiEvent;
  className?: string;
  icon?: React.ComponentType<IconProps>;
  onClick?: () => void;
}

const Stat: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  onlyNumbers: showOnlyNumbers,
  statNameLocaleKey,
  statCount = 0,
  userListType,
  title,
  biData,
  className,
  icon: Icon,
  onClick,
}) => {
  const { t } = useTranslation();
  const onlyNumbers = !statNameLocaleKey || showOnlyNumbers;
  const hasTooltip = userListType && deviation;

  const iconAndStat = (
    <PlainButtonOrLink
      className={s['stat-button']}
      biData={biData}
      title={title}
      onClick={onClick}
      {...(onClick ? { style: { cursor: 'pointer' } } : { tabIndex: -1 })}
      isFakeButton={!onClick}
    >
      {Icon && <Icon className={s['stat-icon']} size={IconSize.SMALL} disableDefaultColors />}
      <span className={s['stat-text']}>
        <span className={s['stat-count']}>{shortenNumber(statCount)}</span>{' '}
        <span className={s['stat-name']}>
          {/* just need the name here */}
          {statCount === 1 ? t(`${statNameLocaleKey}_one`) : t(`${statNameLocaleKey}_other`)}
        </span>
      </span>
    </PlainButtonOrLink>
  );

  return (
    <span
      className={classnames(
        s['stat'],
        hasTooltip && s['has-tooltip'],
        onlyNumbers && s['only-numbers'],
        className
      )}
    >
      {userListType && deviation ? (
        // TODO: there should be a way to open these with the keyboard
        <WhoTooltipWrapper deviation={deviation} userListType={userListType}>
          {iconAndStat}
        </WhoTooltipWrapper>
      ) : (
        iconAndStat
      )}
    </span>
  );
};
Stat.displayName = 'Stat';

export default Stat;
